
import AdUnit from '../components/AdUnit';
import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { MapPin, Plane, Lightbulb, ArrowLeft } from 'lucide-react';
import useReveal from '../hooks/useReveal';
import { PageHero, RainbowTop, ArrowLink } from '../components/shared';
import SaveButton from '../components/SaveButton';
import AffiliateBlock from '../components/AffiliateBlock';
import AdSlot from '../components/AdSlot';
import { EMIRATES } from '../mock';

const EmirateDetail = () => {
  const { slug } = useParams();
  const e = EMIRATES.find((x) => x.slug === slug);
  useReveal([slug]);

  if (!e) {
    return (
      <div className="bg-[#F6F1E3] min-h-[60vh] flex flex-col items-center justify-center text-center px-5">
        <h1 className="font-display text-[32px] font-semibold text-[#1B1D21]">Emirate not found</h1>
        <Link to="/emirates" className="mt-5 text-[#2F663C] font-semibold underline underline-offset-2">
          ← Back to all emirates
        </Link>
      </div>
    );
  }

  const idx = EMIRATES.findIndex((x) => x.slug === slug);
  const next = EMIRATES[(idx + 1) % EMIRATES.length];

  return (
    <div className="bg-[#F6F1E3]">
      <PageHero image={e.image} alt={e.name} label={`Emirate ${e.number}`} title={e.name} />
      <AdUnit slot="inline" />

      <div className="max-w-3xl mx-auto px-5 pt-8">
        <div className="flex items-center justify-between gap-4">
          <Link to="/emirates" className="inline-flex items-center gap-1.5 text-[14px] font-semibold text-[#2F663C] hover:text-[#1B1D21] transition-colors">
            <ArrowLeft className="w-4 h-4" /> All emirates
          </Link>
          <SaveButton
            variant="pill"
            item={{ type: 'emirate', ref_id: `emirate-${e.slug}`, title: e.name, subtitle: 'Emirate guide', to: `/emirates/${e.slug}` }}
          />
        </div>
        <p className="reveal mt-6 text-[20px] leading-relaxed text-[#3a4f63]">{e.intro}</p>

        {/* Quick facts */}
        <div className="reveal mt-8 grid sm:grid-cols-2 gap-4">
          <div className="rounded-xl border border-[#E6D7A2] bg-[#EFE7D2] p-5">
            <div className="flex items-center gap-2 text-[#e0564e]">
              <MapPin className="w-4 h-4" />
              <span className="text-[12px] font-semibold tracking-[0.14em] uppercase">Best for</span>
            </div>
            <p className="mt-2 text-[16px] text-[#1B1D21] font-medium">{e.bestFor}</p>
          </div>
          <div className="rounded-xl border border-[#E6D7A2] bg-[#EFE7D2] p-5">
            <div className="flex items-center gap-2 text-[#2F663C]">
              <Plane className="w-4 h-4" />
              <span className="text-[12px] font-semibold tracking-[0.14em] uppercase">Getting there</span>
            </div>
            <p className="mt-2 text-[16px] text-[#1B1D21] font-medium">{e.gettingThere}</p>
          </div>
        </div>

        {/* Highlights */}
        <h2 className="reveal mt-12 font-display text-[30px] font-semibold text-[#1B1D21]">
          What to see &amp; do
        </h2>
        <div className="mt-6 grid sm:grid-cols-2 gap-5">
          {e.highlights.map((h) => (
            <div key={h.title} className="reveal lift bg-white rounded-2xl border border-[#E6D7A2] overflow-hidden shadow-[0_14px_34px_-26px_rgba(27,29,33,0.5)]">
              <RainbowTop />
              <div className="p-6">
                <h3 className="font-display text-[19px] font-semibold text-[#1B1D21]">{h.title}</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-[#4B4F58]">{h.body}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Tip */}
        <div className="reveal mt-10 rounded-2xl bg-[#1B1D21] text-white p-7">
          <div className="flex items-center gap-2 text-[#CBA344]">
            <Lightbulb className="w-5 h-5" />
            <span className="text-[12px] font-semibold tracking-[0.16em] uppercase">Local tip</span>
          </div>
          <p className="mt-3 text-[17px] leading-relaxed text-[#dbe5ee]">{e.tip}</p>
        </div>

        {/* Tours & essentials (affiliate) */}
        <div className="reveal mt-12">
          <AffiliateBlock placement="emirate" title={`Book things to do in ${e.name}`} subtitle="Tours, tickets and transfers from trusted partners." columns={2} />
        </div>

        {/* Ad */}
        <div className="reveal mt-10">
          <AdSlot slot="inContent" minH={140} />
        </div>

        {/* Next */}
        <div className="reveal my-14 flex items-center justify-between gap-4 rounded-2xl border border-[#E6D7A2] bg-[#EFE7D2] p-6">
          <div>
            <span className="text-[12px] font-semibold tracking-[0.14em] uppercase text-[#e0564e]">Next emirate</span>
            <p className="mt-1 font-display text-[22px] font-semibold text-[#1B1D21]">{next.name}</p>
          </div>
          <ArrowLink to={`/emirates/${next.slug}`}>Read the guide</ArrowLink>
        </div>
      </div>
    </div>
  );
};

export default EmirateDetail;
